import { Loader2, ShieldCheck, X } from 'lucide-react'
import { useEffect } from 'react'

interface HmrcAuthModalProps {
  isOpen: boolean
  onClose: () => void
  authUrl: string | null
  isRequestingAuth: boolean
  onAuthorize: () => void
  filingType: string
}

export default function HmrcAuthModal({
  isOpen,
  onClose,
  authUrl,
  isRequestingAuth,
  onAuthorize,
  filingType,
}: HmrcAuthModalProps) {
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleAuthorize = () => {
    if (authUrl) {
      // Store destination route for after auth completion if not already set
      if (!sessionStorage.getItem('hmrcAuthRedirect')) {
        // If not already set, we'll set it based on filingType
        const routeMap: Record<string, string> = {
          'add-company': '/add-company',
          'self-assessment': '/add-self-assessment',
          'self-assessment-sa': '/add-self-assessment?flow=sa',
          'self-assessment-trad': '/add-self-assessment?flow=traditional',
          'ct600-filing': '/ct600-filing',
          'companies-house-filing': '/companies-house-filing',
        }
        const route = routeMap[filingType] || '/dashboard'
        sessionStorage.setItem('hmrcAuthRedirect', route)
      }
      window.location.href = authUrl
    } else {
      onAuthorize()
    }
  }

  return (
    <div
      className='fixed inset-0 flex items-center justify-center z-50 p-4'
      style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
      role='presentation'
    >
      <div
        className='bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-auto relative'
        onClick={event => event.stopPropagation()}
        role='dialog'
        aria-modal='true'
        tabIndex={-1}
      >
        {/* Header */}
        <div className='px-8 pt-8 pb-6 border-b border-gray-100'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className='w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center'>
                <ShieldCheck className='w-6 h-6 text-amber-600' />
              </div>
              <div>
                <h2 className='text-2xl font-bold text-gray-900'>
                  HMRC Authorization Required
                </h2>
                <p className='text-gray-500 text-sm mt-1'>
                  Complete authorization to continue
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className='w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors'
            >
              <X className='w-5 h-5 text-gray-600' />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className='px-8 py-6'>
          <div className='mb-6'>
            <p className='text-gray-700 text-base leading-relaxed'>
              HMRC authorization is required before you can access filing services.
              This allows us to securely connect to HMRC systems on your behalf.
            </p>
          </div>

          <div className='bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6'>
            <p className='text-sm text-amber-900'>
              <strong className='font-semibold'>What to expect:</strong>
            </p>
            <ul className='list-disc list-inside text-sm text-amber-800 mt-2 space-y-1'>
              <li>You&apos;ll be redirected to the HMRC authorization portal</li>
              <li>Sign in with your Government Gateway credentials</li>
              <li>Grant the necessary permissions</li>
              <li>You&apos;ll be redirected back to continue your filing</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className='flex flex-col gap-3'>
            <button
              onClick={handleAuthorize}
              disabled={isRequestingAuth || !authUrl}
              className='w-full inline-flex items-center justify-center gap-2 rounded-full bg-gray-900 px-6 py-3 text-base font-semibold text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            >
              {isRequestingAuth ? (
                <>
                  <Loader2 className='h-5 w-5 animate-spin' />
                  <span>Preparing authorization...</span>
                </>
              ) : (
                <>
                  <ShieldCheck className='h-5 w-5' />
                  <span>Authorize with HMRC</span>
                </>
              )}
            </button>
            <button
              onClick={onClose}
              disabled={isRequestingAuth}
              className='w-full rounded-full border border-gray-300 px-6 py-3 text-base font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 transition-colors'
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
